import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Send, Megaphone, Users, Check, CheckCheck, Filter } from "lucide-react";
import { fmtDateTime } from "@/lib/format";

// System roles that can compose and send announcements. Everyone else (cast,
// support_staff) gets a read-only feed — they still need to see what the
// production team broadcasts.
const CAN_BROADCAST = new Set(["org_admin", "production_staff", "producer", "super_admin"]);

export default function Announcements() {
  const { user } = useAuth();
  const canBroadcast = CAN_BROADCAST.has(user?.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", body: "", audience: "all" });
  const [unreadOnly, setUnreadOnly] = useState(false);
  const load = () => api.get("/announcements").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const unreadCount = items.filter((a) => !a.is_read).length;
  const visible = unreadOnly ? items.filter((a) => !a.is_read) : items;

  const markOne = async (id) => {
    // Optimistic — the endpoint is idempotent so a network failure just
    // leaves us where we started.
    setItems((cur) => cur.map((a) => (a.id === id ? { ...a, is_read: true } : a)));
    try { await api.post(`/announcements/${id}/read`); }
    catch { toast.error("Couldn't save read status"); load(); }
  };
  const markAll = async () => {
    if (unreadCount === 0) return;
    setItems((cur) => cur.map((a) => ({ ...a, is_read: true })));
    try {
      const { data } = await api.post("/announcements/read-all");
      toast.success(`Marked ${data.marked || unreadCount} as read`);
    } catch { toast.error("Couldn't mark all as read"); load(); }
  };

  const send = async () => {
    if (!form.subject.trim() || !form.body.trim()) return toast.error("Subject and body required");
    const r = await api.post("/announcements", form);
    toast.success(`Sent to ${r.data.recipient_count} recipient(s) — mock delivery`);
    setOpen(false);
    setForm({ subject: "", body: "", audience: "all" });
    load();
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="announcements-page">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Broadcast</div>
          <h1 className="mt-1 font-display text-4xl tracking-tight font-light">
            {canBroadcast ? "Email Announcements" : "Announcements"}
          </h1>
          <p className="mt-1 text-zinc-400 text-sm">
            {canBroadcast
              ? <>Compose &amp; send updates to the company. <span className="text-amber-400 font-semibold uppercase text-xs tracking-widest">Mock delivery — logged only</span>.</>
              : <>Every broadcast from the production team, most recent first.</>
            }
          </p>
        </div>
        {canBroadcast && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-announcement-btn" className="bg-gold text-black hover:bg-gold-hover"><Megaphone className="w-4 h-4 mr-1" />New announcement</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg">
            <DialogHeader><DialogTitle className="font-display">Compose</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Audience</Label>
                <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    <SelectItem value="all">Everyone</SelectItem>
                    <SelectItem value="cast">Cast only</SelectItem>
                    <SelectItem value="staff">Staff only</SelectItem>
                    <SelectItem value="admin">Admins only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Subject</Label><Input data-testid="announcement-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
              <div><Label>Body</Label><Textarea rows={6} data-testid="announcement-body-input" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
            </div>
            <DialogFooter><Button data-testid="send-announcement-btn" onClick={send} className="bg-gold text-black hover:bg-gold-hover"><Send className="w-4 h-4 mr-1" />Send</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </header>

      <div className="flex items-center gap-2 flex-wrap" data-testid="announcements-toolbar">
        <button
          data-testid="unread-only-toggle"
          onClick={() => setUnreadOnly((v) => !v)}
          className={`h-8 px-3 rounded-full border text-xs inline-flex items-center gap-1.5 transition-colors ${
            unreadOnly ? "bg-gold text-black border-gold font-semibold" : "border-white/15 text-zinc-300 hover:bg-white/5"
          }`}
        >
          <Filter className="w-3.5 h-3.5" /> Unread only
          <span className={`font-mono ${unreadOnly ? "text-black/80" : "text-gold"}`}>{unreadCount}</span>
        </button>
        {unreadCount > 0 && (
          <button
            data-testid="mark-all-read-btn"
            onClick={markAll}
            className="h-8 px-3 rounded-full border border-white/15 text-zinc-300 hover:bg-white/5 text-xs inline-flex items-center gap-1.5"
          >
            <CheckCheck className="w-3.5 h-3.5" /> Mark all as read
          </button>
        )}
      </div>

      <div className="space-y-3">
        {visible.map((a) => (
          <div
            key={a.id}
            data-testid={`announcement-${a.id}`}
            className={`rounded-lg border p-5 transition-colors ${
              a.is_read ? "border-white/5 bg-[#0F0F0F] opacity-70" : "border-gold/25 bg-[#121212]"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-[10px] uppercase tracking-widest text-gold">To {a.audience}</div>
                  {!a.is_read && (
                    <span data-testid={`unread-dot-${a.id}`} className="w-1.5 h-1.5 rounded-full bg-gold" aria-label="Unread" />
                  )}
                </div>
                <div className="font-display text-lg mt-1">{a.subject}</div>
              </div>
              <div className="flex items-center gap-3 text-xs text-zinc-500 shrink-0">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid={`announcement-recipients-${a.id}`}
                      className="inline-flex items-center gap-1 hover:text-white focus:text-white focus:outline-none cursor-help"
                      aria-label={`View ${a.recipient_count} recipients`}
                    >
                      <Users className="w-3.5 h-3.5" /> {a.recipient_count}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="left"
                    align="start"
                    className="w-72 max-h-72 overflow-y-auto bg-[#0A0A0A] border-white/10 text-xs text-zinc-200 p-3"
                    data-testid={`announcement-recipients-popover-${a.id}`}
                  >
                    <div className="text-[10px] uppercase tracking-widest text-gold mb-2">
                      Sent to {a.recipient_count} {a.recipient_count === 1 ? "person" : "people"}
                    </div>
                    {Array.isArray(a.recipient_details) && a.recipient_details.length > 0 ? (
                      <ul className="space-y-1.5">
                        {a.recipient_details.map((r, i) => (
                          <li key={`${r.email || i}`} className="leading-tight">
                            <div className="text-zinc-100 truncate">{r.name || r.email}</div>
                            {r.name && r.email && (
                              <div className="text-[10px] text-zinc-500 truncate">{r.email}</div>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : Array.isArray(a.recipients) && a.recipients.length > 0 ? (
                      // Fallback for legacy announcements — we only saved
                      // emails, not names. Better than showing nothing.
                      <ul className="space-y-1">
                        {a.recipients.map((email, i) => (
                          <li key={`${email}-${i}`} className="text-zinc-300 truncate">{email}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-zinc-500 italic">Recipient list not recorded for this announcement.</div>
                    )}
                  </PopoverContent>
                </Popover>
                {!a.is_read && (
                  <button
                    data-testid={`mark-read-${a.id}`}
                    onClick={() => markOne(a.id)}
                    className="h-7 px-2 rounded border border-white/10 text-[11px] hover:bg-white/5 hover:text-white inline-flex items-center gap-1"
                  >
                    <Check className="w-3 h-3" /> Mark read
                  </button>
                )}
              </div>
            </div>
            <div className="mt-2 text-sm text-zinc-400 whitespace-pre-wrap">{a.body}</div>
            <div className="mt-3 text-[11px] text-zinc-500 flex items-center gap-2">
              <span>{a.sent_by_name}</span>·<span>{fmtDateTime(a.sent_at)}</span>·
              <span className="uppercase tracking-widest text-amber-400">{a.delivery}</span>
            </div>
          </div>
        ))}
        {visible.length === 0 && (
          <div className="text-sm text-zinc-500 border border-dashed border-white/10 rounded-md p-8 text-center">
            {items.length === 0
              ? "No announcements yet."
              : unreadOnly
                ? "You're all caught up. Nothing unread."
                : "No announcements match."}
          </div>
        )}
      </div>
    </div>
  );
}
